import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../utils/api";
import { HiOutlineEye } from "react-icons/hi";
import Modal from "../components/Modal";
import AddPayout from "../components/payout/AddPayout";
import ViewPayout from "../components/payout/ViewPayout";
import EditPayout from "../components/payout/EditPayout";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";

export default function Payouts() {
  const { contentOwnerId } = useParams();

  const [payouts, setPayouts] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [contentOwner, setContentOwner] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalPayoutAmount, setTotalPayoutAmount] = useState(0);
  const itemsPerPage = 5;

  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [isModalOpenForView, setIsModalOpenForView] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);

  const totalEarnings = earnings.reduce(
    (sum, e) => sum + (e.netEarning || 0),
    0,
  );
  const outstandingAmount = Math.max(0, totalEarnings - totalPayoutAmount);

  const fetchPayouts = async (page = 1, q = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/payouts/content-owner/${contentOwnerId}`, {
        params: { page, limit: itemsPerPage, q },
        withCredentials: true,
      });
      setPayouts(res.data.payouts || []);
      setTotalPayoutAmount(res.data.totalPayoutAmount || 0);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
        setCurrentPage(res.data.pagination.currentPage || page);
      }
    } catch (err) {
      console.error("Error fetching payouts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contentOwnerId) return;

    api
      .get(`/earnings/content-owner/${contentOwnerId}`, {
        withCredentials: true,
      })
      .then((res) => {
        setEarnings(Array.isArray(res.data.earnings) ? res.data.earnings : []);
      })
      .catch((err) => console.error("Error fetching earnings:", err));
  }, [contentOwnerId]);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    if (!contentOwnerId) return;
    api
      .get(`/contentOwners/${contentOwnerId}`, { withCredentials: true })
      .then((res) => {
        setContentOwner(res.data);
        if (res.data.productId) {
          api
            .get(`/products/${res.data.productId}`, { withCredentials: true })
            .then((productRes) => setProduct(productRes.data))
            .catch((err) => console.error("Error fetching product:", err));
        }
      })
      .catch((err) => console.error("Error fetching content owner:", err));
  }, [contentOwnerId]);

  useEffect(() => {
    if (!contentOwnerId) return;
    fetchPayouts(currentPage, search);
  }, [contentOwnerId, currentPage, search]);

  const deletePayout = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payout?")) return;

    try {
      await api.delete(`/payouts/${id}`, { withCredentials: true });
      if (payouts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchPayouts(currentPage, search);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete payout!");
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setCurrentPage(1);
    setSearch("");
  };

  const paginatedPayouts = payouts;

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddPayout
          contentOwnerId={contentOwnerId}
          onSuccess={async () => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchPayouts(1, search);
          }}
        />
      </Modal>
      <Modal
        isOpen={isModalOpenForView}
        onClose={() => {
          setIsModalOpenForView(false);
          setSelectedPayout(null);
        }}
      >
        {selectedPayout && <ViewPayout payout={selectedPayout} />}
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedPayout && (
          <EditPayout
            payout={selectedPayout}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchPayouts(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div>
        <div className="flex flex-col items-center mt-10 gap-2">
          {product && (
            <>
              <img
                src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
                alt={product.name}
                className="w-14 h-14 object-cover rounded-xl"
              />
              <h1 className="text-2xl font-bold">{product.name}</h1>
              <h2 className="text-xl font-semibold">Payouts</h2>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 ml-2 mr-2 mt-5 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-600 text-sm font-medium">
              Total Net Earnings
            </p>
            <p className="text-2xl font-bold text-blue-600 mt-2">
              ${totalEarnings.toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-600 text-sm font-medium">Total Payouts</p>
            <p className="text-2xl font-bold text-green-600 mt-2">
              ${totalPayoutAmount.toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-600 text-sm font-medium">
              Outstanding Amount
            </p>
            <p
              className={`text-2xl font-bold mt-2 ${outstandingAmount > 0 ? "text-red-600" : "text-green-600"}`}
            >
              ${outstandingAmount.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex">
              {userRole && ["Admin", "Manager"].includes(userRole) && (
                <AddBtn
                  onClick={() => setIsModalOpenForAdd(true)}
                  label="Add Payout"
                />
              )}
            </div>
            <div>
              {contentOwner && (
                <div className="text-gray-700">
                  Content Owner:{" "}
                  <span className="font-bold text-blue-600">
                    {contentOwner.name}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4 flex justify-end gap-2">
            <input
              type="date"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-40 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-400 text-white font-medium rounded-lg hover:bg-gray-500 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                  Paid At
                </th>
                <th className="px-4 py-3 text-center">Amount</th>
                <th className="px-4 py-3 text-center">Payment Method</th>
                <th className="px-4 py-3 text-center">Note</th>
                <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading payouts...
                  </td>
                </tr>
              ) : paginatedPayouts.length ? (
                paginatedPayouts.map((payout) => (
                  <tr
                    key={payout._id}
                    className="border-t transition hover:bg-slate-100"
                  >
                    <td className="px-4 py-4">
                      {new Date(payout.paidAt).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center font-semibold">
                      ${payout.amount}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {payout.paymentMethod}
                    </td>
                    <td className="px-4 py-4 text-center text-gray-600">
                      {payout.note || "-"}
                    </td>

                    <td className="px-4 py-4 flex justify-end gap-4">
                      <button
                        onClick={() => {
                          setSelectedPayout(payout);
                          setIsModalOpenForView(true);
                        }}
                      >
                        <HiOutlineEye className="h-5 w-5 transform hover:scale-110 text-green-400 hover:text-green-700 transition" />
                      </button>
                      {userRole && ["Admin", "Manager"].includes(userRole) && (
                        <>
                          <EditBtn
                            onClick={() => {
                              setSelectedPayout(payout);
                              setIsModalOpenForEdit(true);
                            }}
                            label="Edit Payout"
                          />
                          <DeleteBtn
                            onClick={() => deletePayout(payout._id)}
                            label="Delete Payout"
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No payouts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
